import { Link } from "react-router-dom";
import { Users, PawPrint, Maximize2, Coffee } from "lucide-react";
import { Img } from "@/components/common/Primitives";
import { SIZES, imgProps } from "@/lib/images";
import { useLang } from "@/lib/i18n";
import { minPrice } from "@/lib/content";
import { nok } from "@/lib/format";
import { BADGE_LABELS } from "@/lib/booking";

const Spec = ({ icon: I, children }) => (
  <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><I className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} aria-hidden /> {children}</span>
);

/** Photo-first room card (listing-style) with spec chips: m² · guests · breakfast · dog */
export const RoomCard = ({ rt, testid, eager = false, aspect = "aspect-[4/3]", position, src, sizes = SIZES.third }) => {
  const { lang, tx, tr } = useLang();
  const id = testid || `room-card-${rt.id}`;
  const badge = (rt.badges || []).find((b) => ["sea_view", "suite", "best_value", "family"].includes(b));
  const main = src || rt.images?.[0];
  const second = (rt.images || []).find((i) => i !== main) || null;
  return (
    <Link to={`/rom/${rt.slug}`} className="group block rounded-[22px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4" data-testid={id}>
      <div className="relative">
        <Img src={main} alt={tr(rt.name)} className={`img-zoom ${aspect}`} radius="rounded-[22px]" eager={eager} sizes={sizes} position={position}>
          {second && (
            <img
              {...imgProps(second, sizes)}
              alt=""
              loading="lazy"
              decoding="async"
              aria-hidden
              className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 ease-premium group-hover:opacity-100 motion-reduce:hidden"
              data-testid={`${id}-hover-image`}
            />
          )}
        </Img>
        {badge && <span className="absolute left-3 top-3 rounded-full bg-card/95 px-3 py-1.5 text-xs font-semibold text-foreground shadow-float backdrop-blur" data-testid={`${id}-badge`}>{tr(BADGE_LABELS[badge])}</span>}
      </div>
      <div className="mt-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-[1.0625rem] font-semibold leading-snug tracking-[-0.015em] text-foreground">{tr(rt.name)}</h3>
          <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground" data-testid={`${id}-specs`}>
            {rt.size_m2 && <Spec icon={Maximize2}>{rt.size_m2} m²</Spec>}
            <Spec icon={Users}>{tx(`Inntil ${rt.max_guests}`, `Up to ${rt.max_guests}`)}</Spec>
            <Spec icon={Coffee}>{tx("Frukost", "Breakfast")}</Spec>
            {rt.dogs_allowed && <Spec icon={PawPrint}>{tx("Hund", "Dog")}</Spec>}
          </p>
        </div>
        <p className="shrink-0 text-right">
          <span className="block text-[1.0625rem] font-semibold tnum text-foreground" data-testid={`${id}-price`}>{nok(minPrice(rt), lang)}</span>
          <span className="block text-xs text-muted-foreground">{tx("per natt", "per night")}</span>
        </p>
      </div>
    </Link>
  );
};
